import type { ConfigType } from '@plone/registry';
import schemaFormBlock from '../schemaFormBlock';
import eventRegistrationFormBlock from '../eventRegistrationFormBlock';
import { skipBlocks } from '../../transformers/blocks/blocks';
import blockWrapperFactory, {
  sectionBlocks,
} from '../../components/Blocks/BlockWrapper/BlockWrapper';
import blockWrapperSchemaEnhancer from '../../components/Blocks/BlockWrapper/schema';
import restrictBlock from '../restrictedBlocks';
import { cloneContainerBlock } from '../../helpers/Blocks/cloneContainerBlock';
import { configureListingBlocks } from './listingBlocks';
import { configureContentBlocks } from './contentBlocks';
import {
  configureMediaBlocks,
  configureLayoutBlocks,
  configureEduProductBlocks,
  configureTextBlocks,
} from './mediaAndLayoutBlocks';

/**
 * Blocks that nest other blocks below `data` instead of on the block itself.
 * See helpers/Blocks/cloneContainerBlock.ts (#1728).
 */
const CONTAINER_BLOCKS = ['group', 'accordion', 'tabs_block'];

/**
 * Configure all blocks
 */
export const configureBlocks = (config: ConfigType) => {
  // Configure initial blocks and required blocks
  config.blocks.requiredBlocks = [];

  // Configure group blocks order - defines the order of block categories in the block chooser
  config.blocks.groupBlocksOrder = [
    { id: 'text', title: 'Text' },
    { id: 'listingsearch', title: 'Auflistungen / Suche' },
    { id: 'media', title: 'Bilder / Videos' },
    { id: 'layout', title: 'Layout' },
    { id: 'content', title: 'Inhalt' },
    { id: 'eduproduct', title: 'Produktinformationen' },
    { id: 'common', title: 'Allgemein' },
  ];

  // Configure different block categories
  config = configureTextBlocks(config);
  config = configureListingBlocks(config);
  config = configureMediaBlocks(config);
  config = configureLayoutBlocks(config);
  config = configureContentBlocks(config);
  config = configureEduProductBlocks(config);

  // Configure form blocks
  config = schemaFormBlock(config);
  config = eventRegistrationFormBlock(config);
  config.blocks.blocksConfig.form = {
    ...config.blocks.blocksConfig.form,
    restricted: true,
  };

  // Remove volto-light-theme schema enhancer from video block
  delete config.blocks.blocksConfig.video.schemaEnhancer;

  // #1728: container blocks keep their nested blocks under `data`, a shape
  // Volto's own `cloneBlocks` does not descend into — copies kept the nested
  // block ids (and the very same object), so editing the copy changed the
  // original. `cloneData` is the hook `pasteBlocks` prefers over `cloneBlocks`.
  CONTAINER_BLOCKS.forEach((id) => {
    const block = (config.blocks.blocksConfig as any)[id];
    if (block) {
      block.cloneData = cloneContainerBlock;
    }
  });

  // Remove blocks that are marked to be skipped
  skipBlocks.forEach((key) => {
    if (key in (config.blocks.blocksConfig as any)) {
      delete (config.blocks.blocksConfig as any)[key];
    }
  });

  // Apply block wrapper and restrictions to all blocks
  const keys = Object.keys(config.blocks.blocksConfig);
  for (const i in keys) {
    let block = (config.blocks.blocksConfig as any)[keys[i]];
    if (!block?.id) {
      continue;
    }
    // Apply block wrapper factory to view and edit components
    block.view = blockWrapperFactory(block.view, false);
    block.edit = blockWrapperFactory(block.edit, true);
    block.restricted = restrictBlock(block.restricted);

    // Add schema enhancer for section blocks
    if (sectionBlocks.includes(keys[i])) {
      // Provide default empty schema for blocks without their own schema
      if (!('schema' in block)) {
        block.schema = (props: any) => ({
          properties: {},
          fieldsets: [],
        });
      }
      block.schemaEnhancer = blockWrapperSchemaEnhancer(block.schemaEnhancer);
    }

    (config.blocks.blocksConfig as any)[keys[i]] = block;
  }

  // Clean up belowContent slot by removing tags and related items
  if (config.slots?.belowContent?.slots) {
    config.slots.belowContent.slots = config.slots.belowContent.slots.filter(
      (key: any) => key !== 'tags' && key !== 'relatedItems',
    );
  }

  return config;
};
